<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {Check, CircleCheck, CirclePlay, Info, Video} from "@lucide/vue";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import useBirthdayStockVideos from "@/birthday-gift/composables/useBirthdayStockVideos";
import {GiftVideo} from "@/birthday-gift/interfaces";

/**
 * The birthday video section of the gift form. Each gift carries its own
 * video, joined to the message sent to the patient.
 */
const props = defineProps<{
    modelValue: GiftVideo;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: GiftVideo): void;
}>();

const {data: videos, isLoading} = useBirthdayStockVideos();

const youtubeUrl = ref('');

watch(() => props.modelValue, (video) => {
    youtubeUrl.value = video.video_type === 'youtube' ? video.video_url ?? '' : '';
}, {immediate: true});

const isYoutube = computed(() => props.modelValue.video_type === 'youtube');

const validYoutubeUrl = computed(() => /^https?:\/\/([a-z0-9-]+\.)*(youtube\.com|youtu\.be)\//i
    .test(youtubeUrl.value.trim()));

const hint = computed(() => {
    if (!isYoutube.value) {
        return props.modelValue.video_id
            ? 'Cette vidéo sera jointe au message de ce cadeau.'
            : 'Aucune vidéo : le message partira sans bouton vidéo.';
    }

    return validYoutubeUrl.value
        ? 'Lien YouTube valide — cette vidéo sera envoyée.'
        : 'Collez un lien YouTube pour activer votre vidéo.';
});

const selectStock = (videoId: number) => {
    emit('update:modelValue', {
        video_type: 'stock',
        // Clicking the selected video again clears it.
        video_id: !isYoutube.value && props.modelValue.video_id === videoId ? null : videoId,
        video_url: null,
    });
};

const applyYoutube = () => {
    if (!validYoutubeUrl.value) {
        return;
    }

    emit('update:modelValue', {
        video_type: 'youtube',
        video_id: null,
        video_url: youtubeUrl.value.trim(),
    });
};
</script>

<template>
  <div class="flex flex-col gap-3 border-t border-gray-light pt-4">
    <div class="flex flex-wrap items-baseline gap-2">
      <Label>La vidéo d'anniversaire</Label>
      <span class="text-xs text-muted-foreground">jointe au message de ce cadeau</span>
    </div>

    <BaseFlexSpinner v-if="isLoading" />
    <div
      v-else
      class="grid grid-cols-2 gap-3 lg:grid-cols-4"
    >
      <button
        v-for="video in videos ?? []"
        :key="video.id"
        type="button"
        class="flex flex-col overflow-hidden rounded-lg border-2 border-solid text-left transition-colors"
        :class="!isYoutube && modelValue.video_id === video.id ? 'border-primary' : 'border-gray hover:border-primary'"
        @click="selectStock(video.id)"
      >
        <span class="relative flex h-[72px] items-center justify-center bg-lightPurple-lightest">
          <Video class="h-6 w-6 text-primary" />
          <span
            v-if="!isYoutube && modelValue.video_id === video.id"
            class="absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white"
          >
            <Check class="h-3 w-3" />
          </span>
        </span>
        <span class="block px-2.5 py-2 text-xs font-semibold leading-snug">{{ video.title }}</span>
      </button>
    </div>

    <div>
      <div
        class="flex w-full items-center gap-2.5 rounded-lg border-[1.5px] border-solid px-3 py-2.5"
        :class="isYoutube ? 'border-primary bg-primary-lightest' : 'border-gray bg-white'"
      >
        <CirclePlay class="h-5 w-5 text-[#e0322b]" />
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-semibold">Ma propre vidéo</span>
          <span class="block text-xs text-muted-foreground">
            Collez le lien YouTube d'une vidéo tournée par votre équipe
          </span>
        </span>
      </div>
      <Input
        v-model="youtubeUrl"
        placeholder="https://youtu.be/…"
        class="mt-2.5 w-full"
        @blur="applyYoutube"
        @keyup.enter="applyYoutube"
      />
      <div
        class="mt-1.5 flex items-center gap-1.5 text-xs"
        :class="isYoutube && validYoutubeUrl ? 'text-secondary-darkest' : 'text-muted-foreground'"
      >
        <component
          :is="isYoutube && validYoutubeUrl ? CircleCheck : Info"
          class="h-3.5 w-3.5"
        />{{ hint }}
      </div>
    </div>
  </div>
</template>
